import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Avatar,
  Box,
  Button,
  TextField,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
  Menu,
  MenuItem,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import ThumbUp from '@mui/icons-material/ThumbUp';
import ThumbDown from '@mui/icons-material/ThumbDown';
import CommentIcon from '@mui/icons-material/Comment';
import Share from '@mui/icons-material/Share';
import MoreVert from '@mui/icons-material/MoreVert';
import Send from '@mui/icons-material/Send';
import Search from '@mui/icons-material/Search';
import FilterList from '@mui/icons-material/FilterList';
import Bookmark from '@mui/icons-material/Bookmark';
import BookmarkBorder from '@mui/icons-material/BookmarkBorder';
import SmartToy from '@mui/icons-material/SmartToy';
import { useAuth } from '../context/AuthContext';
import { useCommunity, Discussion, type Comment } from '../context/CommunityContext';
import VisitorMetrics from '../components/analytics/VisitorMetrics';

// Mock data for AI chat responses
const mockAIResponses = {
  'drought resistant maize': 'For drought-resistant maize varieties in Kenya, consider KDV-1, KDV-6, or Sungura 301. These varieties have shown good performance in dry regions with as little as 250mm of rainfall per season. Complement these with water conservation techniques like mulching and tied ridges to maximize soil moisture retention.',
  'tomato blight': 'To manage tomato blight in humid conditions, try these approaches: 1) Apply copper-based fungicides as a preventative measure before symptoms appear, 2) Ensure proper spacing between plants to improve air circulation, 3) Water at the base of plants in the morning to allow foliage to dry during the day, 4) Consider resistant varieties like Prostar F1 or Anna F1 for future plantings.',
  'solar irrigation': 'Solar-powered irrigation systems typically pay for themselves within 2-3 years compared to fuel-powered pumps. For a 5-acre farm in Kenya, expect initial costs between KSh 150,000-300,000 depending on system capacity and complexity. Maintenance costs are minimal, mainly panel cleaning and occasional pump servicing. Many farmers report 30-40% increase in yield due to consistent irrigation.',
  'avocado prices': 'Current market prices for Hass avocados in Kenya range from KSh 15-20 per piece in local markets to KSh 25-30 for export quality. The export market is indeed seeing higher prices (up to KSh 80-100 per kg) due to increased demand in Europe and Middle East. Consider joining farmer cooperatives for better export market access or explore direct sales to hotels and restaurants for better margins than local markets.',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`community-tabpanel-${index}`}
      aria-labelledby={`community-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CommunityHub: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { discussions, savedPosts, addDiscussion, toggleSavePost, toggleLikePost, getComments, addComment } = useCommunity();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [filteredDiscussions, setFilteredDiscussions] = useState(discussions);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sortOption, setSortOption] = useState('recent');
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostTags, setNewPostTags] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState<any>(null);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleShare = (discussionTitle: string) => {
    navigator.clipboard.writeText(`Check out this discussion: ${discussionTitle}`);
    setSnackbarMessage('Link copied to clipboard!');
    setSnackbarOpen(true);
  };

  const handleViewPost = (discussion: any) => {
    setSelectedDiscussion(discussion);
    setViewDialogOpen(true);
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (selectedDiscussion && viewDialogOpen) {
      getComments(selectedDiscussion.id).then(setComments);
    }
  }, [selectedDiscussion, viewDialogOpen]);

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedDiscussion) return;
    try {
      await addComment(selectedDiscussion.id, newComment);
      setNewComment('');
      // Refresh comments
      getComments(selectedDiscussion.id).then(setComments);
      setSnackbarMessage('Comment added successfully');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Handle search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Handle sort menu
  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setAnchorEl(null);
  };

  const handleSortSelect = (option: string) => {
    setSortOption(option);
    handleSortClose();
  };

  // Handle AI query submission
  const handleAiQuerySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;

    setAiLoading(true);
    
    // Simulate AI response delay
    setTimeout(() => {
      // Check if we have a canned response for any keyword in the query
      const queryLower = aiQuery.toLowerCase();
      let foundResponse = '';
      
      Object.entries(mockAIResponses).forEach(([keyword, response]) => {
        if (queryLower.includes(keyword)) {
          foundResponse = response;
        }
      });
      
      if (foundResponse) {
        setAiResponse(foundResponse);
      } else {
        setAiResponse('I don\'t have specific information on that topic yet. Please try asking about drought resistant crops, tomato diseases, irrigation systems, or market prices for common crops in Kenya.');
      }
      
      setAiLoading(false);
    }, 1500);
  };

  // Handle new post submission
  const handleNewPostSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPostTitle.trim() || !newPostContent.trim()) return;

    addDiscussion({
      title: newPostTitle,
      content: newPostContent,
      tags: newPostTags.split(',').map(tag => tag.trim()).filter(tag => tag),
    });

    setNewPostTitle('');
    setNewPostContent('');
    setNewPostTags('');
    setTabValue(0); // Switch to discussions tab
  };

  // Filter discussions based on search term and tab
  useEffect(() => {
    setLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      let filtered = [...discussions];
      
      // Filter by search term
      if (searchTerm) {
        filtered = filtered.filter(discussion =>
          discussion.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          discussion.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
          discussion.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      
      // Filter by tab
      if (tabValue === 1) { // My Posts
        if (user) {
          filtered = filtered.filter(discussion => discussion.author.id === user.id);
        } else {
          filtered = [];
        }
      } else if (tabValue === 2) { // Saved
        filtered = filtered.filter(discussion => savedPosts.includes(discussion.id));
      }
      
      // Sort discussions
      if (sortOption === 'recent') {
        filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      } else if (sortOption === 'popular') {
        filtered.sort((a, b) => b.likes - a.likes);
      } else if (sortOption === 'commented') {
        filtered.sort((a, b) => b.comments - a.comments);
      }
      
      setFilteredDiscussions(filtered);
      setLoading(false);
    }, 500);
  }, [searchTerm, tabValue, discussions, sortOption, savedPosts]);

  // Format date to relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Community Hub
        </Typography>
        <VisitorMetrics variant="compact" />
      </Box>

      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Discussions" />
          <Tab label="My Posts" />
          <Tab label="Saved" />
          <Tab label="AI Assistant" />
        </Tabs>

        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search discussions by title, content, or tags"
                value={searchTerm}
                onChange={handleSearch}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <Search sx={{ color: 'action.active', mr: 1 }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<MoreVert />}
                  onClick={handleSortClick}
                >
                  Sort
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleSortClose}
                >
                  <MenuItem onClick={() => handleSortSelect('recent')} selected={sortOption === 'recent'}>
                    Most Recent
                  </MenuItem>
                  <MenuItem onClick={() => handleSortSelect('popular')} selected={sortOption === 'popular'}>
                    Most Popular
                  </MenuItem>
                  <MenuItem onClick={() => handleSortSelect('commented')} selected={sortOption === 'commented'}>
                    Most Commented
                  </MenuItem>
                </Menu>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Main content area */}
        <Grid item xs={12} md={8}>
          <TabPanel value={tabValue} index={0}>
            {/* New Post Button */}
            {isAuthenticated && (
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth 
                sx={{ mb: 3 }}
                onClick={() => setTabValue(3)}
              >
                Start a New Discussion
              </Button>
            )}
            
            {/* Discussions List */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredDiscussions.length > 0 ? (
              <Box>
                {filteredDiscussions.map((discussion) => (
                  <Card key={discussion.id} sx={{ mb: 3 }}>
                    <CardHeader
                      avatar={
                        <Avatar src={discussion.author.avatar} alt={discussion.author.name} />
                      }
                      action={
                        <IconButton aria-label="settings">
                          <MoreVert />
                        </IconButton>
                      }
                      title={discussion.author.name}
                      subheader={`${discussion.author.location} • ${formatRelativeTime(discussion.date)}`}
                    />
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {discussion.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" paragraph>
                        {discussion.content}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {discussion.tags.map((tag, index) => (
                          <Chip key={index} label={tag} size="small" color="primary" variant="outlined" />
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                          <ThumbUp fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">{discussion.likes}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                          <CommentIcon fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">{discussion.comments}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ ml: 'auto' }}>
                          {discussion.views} views
                        </Typography>
                      </Box>
                    </CardContent>
                    <Divider />
                    <CardActions>
                      <Button 
                        startIcon={<ThumbUp />} 
                        size="small" 
                        onClick={() => toggleLikePost(discussion.id)}
                        color={discussion.isLiked ? "primary" : "inherit"}
                      >
                        Like ({discussion.likes})
                      </Button>
                      <Button 
                        startIcon={<CommentIcon />} 
                        size="small"
                        onClick={() => handleViewPost(discussion)}
                      >
                        Comment ({discussion.comments})
                      </Button>
                      <Button 
                        startIcon={<Share />} 
                        size="small"
                        onClick={() => handleShare(discussion.title)}
                      >
                        Share
                      </Button>
                      <IconButton 
                        sx={{ ml: 'auto' }} 
                        onClick={() => toggleSavePost(discussion.id)}
                        color={savedPosts.includes(discussion.id) ? 'primary' : 'default'}
                      >
                        {savedPosts.includes(discussion.id) ? <Bookmark /> : <BookmarkBorder />}
                      </IconButton>
                    </CardActions>
                  </Card>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No discussions found matching your criteria
                </Typography>
                <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setSearchTerm('')}>
                  Clear Search
                </Button>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {isAuthenticated ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h6" gutterBottom>
                  You haven't created any posts yet
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  sx={{ mt: 2 }}
                  onClick={() => setTabValue(3)}
                >
                  Create Your First Post
                </Button>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Sign in to view your posts
                </Typography>
                <Button variant="contained" color="primary" sx={{ mt: 2 }}>
                  Sign In
                </Button>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {isAuthenticated ? (
              savedPosts.length > 0 ? (
                <Box>
                  {filteredDiscussions.map((discussion) => (
                    <Card key={discussion.id} sx={{ mb: 3 }}>
                      <CardHeader
                        avatar={
                          <Avatar src={discussion.author.avatar} alt={discussion.author.name} />
                        }
                        title={discussion.author.name}
                        subheader={formatRelativeTime(discussion.date)}
                      />
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {discussion.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {discussion.content.length > 200
                            ? `${discussion.content.substring(0, 200)}...`
                            : discussion.content}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          color="primary"
                          onClick={() => handleViewPost(discussion)}
                        >
                          View Full Post
                        </Button>
                        <IconButton 
                          sx={{ ml: 'auto' }} 
                          onClick={() => toggleSavePost(discussion.id)}
                          color="primary"
                        >
                          <Bookmark />
                        </IconButton>
                      </CardActions>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    You haven't saved any posts yet
                  </Typography>
                  <Typography variant="body1" paragraph>
                    When you find interesting discussions, click the bookmark icon to save them for later.
                  </Typography>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    sx={{ mt: 2 }}
                    onClick={() => setTabValue(0)}
                  >
                    Browse Discussions
                  </Button>
                </Box>
              )
            ) : (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Sign in to view your saved posts
                </Typography>
                <Button variant="contained" color="primary" sx={{ mt: 2 }}>
                  Sign In
                </Button>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {/* Create New Post Form */}
                <Paper sx={{ p: 3, mb: { xs: 3, md: 0 } }}>
                  <Typography variant="h6" gutterBottom>
                    Start a New Discussion
                  </Typography>
                  {isAuthenticated ? (
                    <form onSubmit={handleNewPostSubmit}>
                      <TextField
                        label="Title"
                        fullWidth
                        margin="normal"
                        value={newPostTitle}
                        onChange={(e) => setNewPostTitle(e.target.value)}
                        required
                      />
                      <TextField
                        label="Content"
                        fullWidth
                        margin="normal"
                        multiline
                        rows={6}
                        value={newPostContent}
                        onChange={(e) => setNewPostContent(e.target.value)}
                        required
                      />
                      <TextField
                        label="Tags (comma separated)"
                        fullWidth
                        margin="normal"
                        placeholder="e.g. Maize, Irrigation, Market Prices"
                        value={newPostTags}
                        onChange={(e) => setNewPostTags(e.target.value)}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ mt: 2 }}
                      >
                        Post Discussion
                      </Button>
                    </form>
                  ) : (
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="body1" paragraph>
                        You need to be signed in to create a post.
                      </Typography>
                      <Button variant="contained" color="primary">
                        Sign In
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                {/* AI Assistant */}
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SmartToy color="primary" sx={{ mr: 1, fontSize: 28 }} />
                    <Typography variant="h6">
                      AI Farming Assistant
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Ask questions about farming techniques, crop diseases, market trends, or best practices.
                  </Typography>
                  
                  {aiResponse && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                      <Typography variant="body1">
                        {aiResponse}
                      </Typography>
                    </Paper>
                  )}
                  
                  <form onSubmit={handleAiQuerySubmit}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <TextField
                        fullWidth
                        placeholder="Ask a farming question..."
                        value={aiQuery}
                        onChange={(e) => setAiQuery(e.target.value)}
                        variant="outlined"
                        multiline
                        rows={2}
                        disabled={aiLoading}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ ml: 1, minWidth: '56px', height: '56px' }}
                        disabled={!aiQuery.trim() || aiLoading}
                      >
                        {aiLoading ? <CircularProgress size={24} /> : <Send />}
                      </Button>
                    </Box>
                  </form>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Try asking about:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip 
                        label="Drought resistant crops" 
                        size="small" 
                        onClick={() => setAiQuery('What are the best drought resistant maize varieties for Kenya?')} 
                      />
                      <Chip 
                        label="Tomato diseases" 
                        size="small" 
                        onClick={() => setAiQuery('How do I treat tomato blight in humid conditions?')} 
                      />
                      <Chip 
                        label="Solar irrigation" 
                        size="small" 
                        onClick={() => setAiQuery('Is solar irrigation worth the investment for a small farm?')} 
                      />
                      <Chip 
                        label="Avocado prices" 
                        size="small" 
                        onClick={() => setAiQuery('What are the current market prices for avocados?')} 
                      />
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Popular Topics */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Popular Topics
            </Typography>
            <List dense>
              {['Irrigation Techniques', 'Pest Control', 'Market Prices', 'Organic Farming', 'Climate-Smart Agriculture'].map((topic, index) => (
                <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                  <ListItemText primary={topic} />
                  <Chip 
                    label={Math.floor(Math.random() * 50) + 10} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                  />
                </ListItem>
              ))}
            </List>
            <Button variant="text" fullWidth sx={{ mt: 1 }}>
              View All Topics
            </Button>
          </Paper>

          {/* Active Members */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Active Members
            </Typography>
            <List>
              {[1, 2, 3, 4, 5].map((num) => (
                <ListItem key={num} sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar src={`https://randomuser.me/api/portraits/${num % 2 === 0 ? 'women' : 'men'}/${num}.jpg`} />
                  </ListItemAvatar>
                  <ListItemText 
                    primary={['John Kamau', 'Sarah Ochieng', 'David Mwangi', 'Lucy Wambui', 'Peter Njoroge'][num-1]}
                    secondary={`${Math.floor(Math.random() * 10) + 1} posts this week`}
                  />
                </ListItem>
              ))}
            </List>
            <Button variant="text" fullWidth sx={{ mt: 1 }}>
              View All Members
            </Button>
          </Paper>

          {/* Community Stats */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Community Stats
            </Typography>
            <List dense>
              <ListItem disablePadding sx={{ py: 0.5 }}>
                <ListItemText primary="Total Members" />
                <Typography variant="body1" fontWeight="bold">1,245</Typography>
              </ListItem>
              <ListItem disablePadding sx={{ py: 0.5 }}>
                <ListItemText primary="Active Discussions" />
                <Typography variant="body1" fontWeight="bold">87</Typography>
              </ListItem>
              <ListItem disablePadding sx={{ py: 0.5 }}>
                <ListItemText primary="Posts This Week" />
                <Typography variant="body1" fontWeight="bold">156</Typography>
              </ListItem>
              <ListItem disablePadding sx={{ py: 0.5 }}>
                <ListItemText primary="Comments Today" />
                <Typography variant="body1" fontWeight="bold">42</Typography>
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
      {/* Post View Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedDiscussion && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar src={selectedDiscussion.author.avatar} />
                <Box>
                  <Typography variant="h6">{selectedDiscussion.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Posted by {selectedDiscussion.author.name} • {formatRelativeTime(selectedDiscussion.date)}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body1" paragraph>
                {selectedDiscussion.content}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                {selectedDiscussion.tags.map((tag: string, index: number) => (
                  <Chip key={index} label={tag} size="small" />
                ))}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Comments ({selectedDiscussion.comments})</Typography>

              <List>
                {comments.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No comments yet. Be the first to comment!" />
                  </ListItem>
                ) : (
                  comments.map((comment) => (
                    <React.Fragment key={comment.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar alt={comment.author.name} src={comment.author.avatar} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={comment.author.name}
                          secondary={
                            <React.Fragment>
                              <Typography
                                sx={{ display: 'inline' }}
                                component="span"
                                variant="body2"
                                color="text.primary"
                              >
                                {new Date(comment.date).toLocaleDateString()}
                              </Typography>
                              {" — "}{comment.content}
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  ))
                )}
              </List>

              {/* Add Comment Input */}
              {isAuthenticated ? (
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewComment(e.target.value)}
                  />
                  <Button variant="contained" onClick={handleAddComment} disabled={!newComment.trim()}>
                    Post
                  </Button>
                </Box>
              ) : (
                 <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                   Please log in to comment.
                 </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => toggleLikePost(selectedDiscussion.id)} startIcon={<ThumbUp />}>
                Like ({selectedDiscussion.likes})
              </Button>
              <Button onClick={() => handleShare(selectedDiscussion.title)} startIcon={<Share />}>
                Share
              </Button>
              <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        action={
          <Button color="secondary" size="small" onClick={handleCloseSnackbar}>
            CLOSE
          </Button>
        }
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CommunityHub;